import {
  ChevronLeftIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Drawer from "../commons/Drawer";
import { baseAxios } from "../../apis";
import { IconLoading } from "../../assets/icons";
import { useToast } from "../../context/toastContext";
import { extractErrorMessage } from "../../utils";
import DialogSuccess from "../dialogs/DialogSuccess";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import useAuthedApi from "../../hooks/useAuthedApi";
import { useAuth } from "../../context/authContext";

const DrawerApiConnect = ({ isOpen, onClose, wallet }) => {
  const [openDialog, setOpenDialog] = useState(null);
  const { authedPost } = useAuthedApi();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Form-related state
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [errorText, setErrorText] = useState("");
  const [userIp, setUserIp] = useState("");
  const [seeSecretKey, setSeeSecretKey] = useState(false);
  const [proxyList, setProxyList] = useState([]);
  const [loadingProxies, setLoadingProxies] = useState(true);
  const requiresPassphrase = wallet?.name?.toLowerCase().startsWith("osl") || ["bitget", "okx", "kucoin"].includes(wallet?.name?.toLowerCase());


  // The useMutation hook handles the API call, loading, success, and error states.
  const connectExchangeMutation = useMutation({
    // The mutation function is now a cleaner, single line
    mutationFn: (configData) => authedPost(`/api/api-setting`, configData),
    onSuccess: () => {
      // On success, invalidate the 'exchanges' query to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ["exchanges"] });

      setApiKey("");
      setApiSecret("");
      setPassphrase("");
      setOpenDialog("success");
      handleClose();
      localStorage.setItem("exhchange_connected", "true");
    },
    onError: (err) => {
      const errorResponse = err?.response?.data;
      setErrorText(extractErrorMessage(errorResponse));
    },
  });

  const handleCopyIpAddress = (ip) => {
    navigator.clipboard
      .writeText(ip)
      .then(() => {
        addToast("success", "IP Whitelist addresses copied!", 5000);
      })
      .catch((err) => {
        addToast("error", "Failed to copy IPs.", 5000);
      });
  };

  const handleClose = () => {
    if (connectExchangeMutation.isPending) return;
    setApiKey("");
    setApiSecret("");
    setPassphrase("");
    onClose();
  };

  // The handleSubmit function is now much simpler.
  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorText("");

    if (
      !apiKey.trim() ||
      !apiSecret.trim() ||
      (requiresPassphrase &&
        !passphrase.trim())
    ) {
      setErrorText("Please fill in all fields.");
      return;
    }

    const configData = {
      key: apiKey,
      secret: apiSecret,
      exchange: wallet.id,
    };
    if (!!passphrase) {
      configData.passphrase = passphrase;
    }

    // Just call the mutation; the onSuccess/onError handlers do the rest.
    connectExchangeMutation.mutate(configData);
  };

  useEffect(() => {
    const fetchProxies = async () => {
      try {
        const res = await baseAxios.get("/api/proxy/available");
        const proxies = res?.data?.proxies || [];
        setProxyList(proxies);
      } catch (err) {
        console.error("Failed to fetch proxies:", err);
        setProxyList([]);
      } finally {
        setLoadingProxies(false);
      }
    };

    if (user && isOpen) {
      fetchProxies();
    }
  }, [user, isOpen]);

  useEffect(() => {
    const fetchIp = async () => {
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        const data = await res.json();
        setUserIp(data.ip);
      } catch (error) {
        console.error("Failed to fetch IP address:", error);
      }
    };
    fetchIp();
  }, []);

  return (
    <>
      <DialogSuccess
        open={openDialog === "success"}
        onClose={() => {
          setOpenDialog(null);
          handleClose();
        }}
        title={
          "API connection established – your exchange wallet is now linked."
        }
      />
      <Drawer open={isOpen} width="600px" onClose={handleClose}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between w-full md:px-3">
            <button
              className="bg-transparent hover:bg-transparent text-[#007A78] flex !p-0"
              onClick={onClose}
            >
              <ChevronLeftIcon className="w-[20px] h-[20px]" /> Back
            </button>
            <h2 className="text-base md:text-xl font-bold ">
              {wallet?.connected ? "Change Connected" : "Connect"}&nbsp;
              Exchange&nbsp;
              {wallet?.name}
            </h2>
            <button
              className="bg-transparent hover:bg-transparent text-black !p-0"
              onClick={onClose}
            >
              <XMarkIcon className="w-[20px] h-[20px]" />
            </button>
          </div>

          <div className="flex-grow overflow-y-auto mt-5 p-3 space-y-5">
            <div className="bg-[#8BC3FF]/15 border border-[#007AFF] text-[#21272A]/60 p-3 sm:p-3 rounded-lg  sm:text-sm space-y-2">
              <p className="font-semibold text-sm">
                Step by Step to Connect to API
              </p>
              <ol className="list-decimal list-inside space-y-1.5 pl-1">
                <li>
                  Log in to your exchange account and go to{" "}
                  <p
                    // // // href="#" // Ganti dengan URL API settings yang relevan
                    // // target="_blank"
                    // rel="noopener noreferrer"
                    className="text-sky-600 hover:underline font-medium text-sm inline"
                  >
                    API Settings
                  </p>
                  .
                </li>
                <li>
                Turn on IP whitelisting and copy/paste following list of IP
                  address (these are required for the bot):
                  {/* <div className="mt-1.5 p-2 bg-[#007AFF] rounded text-xs font-mono flex justify-between items-center w-full max-w-[300px] border-2 border-[#007AFF] text-white">
                    {userIp ? (
                      <span className="break-all overflow-hidden mr-1.5">
                        {userIp}
                      </span>
                    ) : (
                      <span className="break-all overflow-hidden mr-1.5">
                        Loading get IP Address...
                      </span>
                    )}

                    <a
                      type="button"
                      onClick={() => handleCopyIpAddress(userIp)}
                      className="text-white hover:text-[#21272A] p-0.5 rounded focus:outline-none focus:ring-1 focus:ring-sky-500 cursor-pointer"
                      aria-label="Copy IP addresses"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4 flex-shrink-0" />
                    </a>
                  </div> */}
                  {loadingProxies ? (
                    <p className="text-sm text-gray-500">
                      Loading proxy whitelist...
                    </p>
                  ) : proxyList.length === 0 ? (
                    <div className="mt-1.5 p-2 bg-[#007AFF] rounded text-xs font-mono flex justify-between items-center w-full max-w-[300px] border-2 border-[#007AFF] text-white">
                      <span className="break-all overflow-hidden mr-1.5">
                        54.169.205.33
                      </span>

                      <a
                        onClick={() => handleCopyIpAddress("54.169.205.33")}
                        className="text-white hover:text-[#21272A] p-0.5 rounded cursor-pointer"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </a>
                    </div>
                  ) : (
                    proxyList.map((ip, idx) => (
                      <div
                        key={idx}
                        className="mt-1.5 p-2 bg-[#007AFF] rounded text-xs font-mono flex justify-between items-center w-full max-w-[300px] border-2 border-[#007AFF] text-white"
                      >
                        <span className="break-all overflow-hidden mr-1.5">
                          {ip}
                        </span>

                        <a
                          onClick={() => handleCopyIpAddress(ip)}
                          className="text-white hover:text-[#21272A] p-0.5 rounded cursor-pointer"
                        >
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        </a>
                      </div>
                    ))
                  )}
                </li>
                <li>Paste generated data in inputs below.</li>
              </ol>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor={`apiKey-${wallet?.id}`}
                  className="block text-sm font-medium text-gray-700 mb-[-3px]"
                >
                  API Key
                </label>
                <input
                  type="text"
                  id={`apiKey-${wallet?.id}`}
                  name="apiKey"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Input API Key"
                  autoComplete="off"
                />
              </div>
              <div>
                <label
                  htmlFor={`apiSecret-${wallet?.id}`}
                  className="block text-sm font-medium text-gray-700 mb-[-3px]"
                >
                  API Secret
                </label>
                <div className="w-full relative">
                  <input
                    type={seeSecretKey ? "text" : "password"}
                    id={`apiSecret-${wallet?.id}`}
                    name="apiSecret"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    placeholder="Input API Secret"
                    className="!pr-[3rem]"
                    autoComplete="off"
                  />
                  <span
                    onClick={() => setSeeSecretKey(!seeSecretKey)}
                    className="absolute top-1/2 right-4 -translate-y-1/2"
                  >
                    {seeSecretKey ? (
                      <EyeSlashIcon className="w-[16px] h-[16px] cursor-pointer" />
                    ) : (
                      <EyeIcon className="w-[16px] h-[16px] cursor-pointer" />
                    )}
                  </span>
                </div>
              </div>
              {requiresPassphrase && (
                <div>
                  <label
                    htmlFor={`passphrase-${wallet?.id}`}
                    className="block text-sm font-medium text-gray-700 mb-[-3px]"
                  >
                    Passphrase
                  </label>
                  <input
                    type="text"
                    id={`passphrase-${wallet?.id}`}
                    placeholder="Input Passphrase"
                    value={passphrase}
                    autoComplete="off"
                    onChange={(e) => setPassphrase(e.target.value)}
                  />
                </div>
              )}
              <div>
                {errorText && (
                  <span className="block text-sm mb-2 text-[#ff0000]">
                    {errorText}
                  </span>
                )}
                <button
                  type="submit"
                  disabled={connectExchangeMutation.isPending}
                  className="w-fit bg-[#007A78] hover:bg-[#007A78] text-white font-semibold py-2.5 px-4 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
                >
                  {connectExchangeMutation.isPending ? (
                    <div className="animate-spin w-fit flex justify-center items-center mx-auto">
                      <IconLoading className="w-[16px] h-[16px]" />
                    </div>
                  ) : (
                    "Connect Exchange Wallet"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default DrawerApiConnect;
